<script setup lang="ts">
import type {
  DungeonChallengeEnvironment,
  DungeonEnvironment,
  DungeonSeason,
} from '~/composables/charon'

const { charon } = useCharon()
const { t } = useI18n()
const route = useRoute()

definePageMeta({
  nameKey: 'game-dungeon-leaderboard',
  head: {
    imageUrl: '/images/view/game/dungeon-explorer/leaderboard/meta_preview.png',
  },
})

const leaderboardSize = 10
const initialSeason = Math.max(parseInt(route.query.season as string ?? '0'), 0)

const { data: dungeonSeasons } = useAsyncData(
  `dungeon-seasons`,
  () => charon<DungeonSeason[]>(`/dungeons/seasons`),
  {
    lazy: false,
    server: true,
  },
)

const { data: dungeonEnvironments } = useAsyncData(
  `dungeon-environments`,
  () => charon<DungeonEnvironment[]>(`/dungeons/environments`),
  {
    lazy: false,
    server: true,
  },
)

const { data: dungeonChallenges } = useAsyncData(
  () => charon<DungeonChallengeEnvironment[]>(
    `/dungeons/challenge-environments`,
    {
      params: {
        size: 5,
      },
    },
  ),
  {
    lazy: false,
    server: true,
  },
)
</script>

<template>
  <Container>
    <HeroCard
      :heading="t('jc.view.dungeon.leaderboard.hero.title')"
      :paragraph="t('jc.view.dungeon.leaderboard.hero.text')"
      :image="{
        src: 'images/view/game/dungeon-explorer/leaderboard/hero_banner.png',
        alt: t('jc.view.dungeon.leaderboard.hero.banner.alt'),
      }"
      :float="{
        src: 'images/view/game/dungeon-explorer/leaderboard/float.png',
      }"
    />
  </Container>

  <Container>
    <Heading
      icon="swords"
      :label="t('jc.view.dungeon.leaderboard.arena.label')"
    />

    <DungeonArenaLeaderboard
      v-if="dungeonSeasons && dungeonEnvironments"
      :leaderboard-size="leaderboardSize"
      :initial-season="initialSeason"
      :seasons="dungeonSeasons"
      :environments="dungeonEnvironments"
    />
  </Container>

  <Container>
    <Heading
      icon="swords"
      :label="t('jc.view.dungeon.leaderboard.endless.label')"
    />

    <DungeonEndlessLeaderboard
      v-if="dungeonSeasons && dungeonEnvironments"
      :leaderboard-size="leaderboardSize"
      :initial-season="initialSeason"
      :seasons="dungeonSeasons"
      :environments="dungeonEnvironments"
    />
  </Container>

  <Container>
    <Heading
      icon="swords"
      :label="t('jc.view.dungeon.leaderboard.challenge.label')"
    />

    <DungeonChallengeLeaderboard
      v-if="dungeonChallenges"
      :leaderboard-size="leaderboardSize"
      :challenges="dungeonChallenges"
    />
  </Container>
</template>
